import { memo, useState } from "react";

import ZipImage from "@hl/base-components/lib/ZipImage";
import {
  ArrowRightNoFill as ArrowRight,
  ArrowLeftNoFill as ArrowLeft,
} from "@hl/base-components/lib/assets/icons.generated";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { Center, Group, Stack, Text, Title } from "@mantine/core";

import { SupportedMediaType, _CollectionType } from "apollo/graphql.generated";

import { CreationPreviewImage } from "./CollectionMediaUploader";
import { SeriesToken, useCreateCollectionState } from "./context";

const sizePreviewText = (
  collectionType: _CollectionType,
  size?: number | null
) => {
  if (collectionType === _CollectionType.Series) {
    return size ? `${size} items` : "Series size";
  }

  if (collectionType === _CollectionType.GenerativeSeries) {
    return size ? `${size} items` : "Open Series";
  }

  if (collectionType === _CollectionType.LimitedEdition) {
    return size ? `${size} items` : "Edition size";
  }

  if (collectionType === _CollectionType.OpenEdition) {
    return "Open edition";
  }
  if (collectionType === _CollectionType.OneOfOne) {
    return "1 of 1";
  }

  return null;
};

export type CollectionPreviewProps = {
  mediaUrl?: string | null;
  mediaType?: SupportedMediaType | "zip" | null;
  showDetails?: boolean;
  name?: string | null;
  collectionType: _CollectionType;
  showPrice?: boolean;
  size?: number | null;
  price?: number | null;
  currency?: string | null;
};
const CollectionPreview = ({
  mediaUrl,
  mediaType,
  showDetails = false,
  name,
  collectionType,
  showPrice = false,
  size,
  price,
  currency = "",
}: CollectionPreviewProps) => {
  const {
    state: {
      series: { newTokens, uploadedTokens },
    },
  } = useCreateCollectionState();
  const [position, setPosition] = useState<number>(1);

  const seriesPreview = (i: number) => {
    const zippedImage: SeriesToken = tokens[i];
    const image = zippedImage?.imageEntry;

    return image ? (
      <ZipImage image={image} maxWidth={320} maxHeight={320} />
    ) : (
      <CreationPreviewImage url={mediaUrl} mediaType={mediaType} />
    );
  };
  const increment = () => {
    setPosition((position + 1) % tokens.length);
  };
  const decrement = () => {
    // javascript has boogus remainder math
    if (position === 0) {
      setPosition(tokens.length - 1);
    } else {
      setPosition((position - 1) % tokens.length);
    }
  };

  const tokens = newTokens.length ? newTokens : uploadedTokens || [];
  return (
    <Stack spacing={0}>
      <Group position="apart" mb={8}>
        <Text weight={500} size="sm">
          Preview
        </Text>
        <Group>
          {collectionType === _CollectionType.Series && (
            <>
              <ArrowLeft onClick={decrement} />
              <ArrowRight onClick={increment} />
            </>
          )}
        </Group>
      </Group>
      <Center mb="sm">
        {collectionType !== _CollectionType.Series && (
          <CreationPreviewImage url={mediaUrl} mediaType={mediaType} />
        )}
        {collectionType === _CollectionType.Series && seriesPreview(position)}
      </Center>
      {showDetails && (
        <Title
          sx={{
            overflowWrap: "anywhere",
          }}
          order={6}
          size="h6"
          fw={500}
          mb={2}
          color={name ? TEXT_COLOR.PRIMARY : TEXT_COLOR.PLACEHOLDER}
        >
          {name || "Collection name"}
        </Title>
      )}
      {showDetails && (
        <Text size="sm" mb={2}>
          {sizePreviewText(collectionType, size)}
        </Text>
      )}
      {showPrice && (
        <Text size="sm">
          {collectionType === _CollectionType.OneOfOne &&
            `Reserve price: ${price} ${currency}`}
          {collectionType !== _CollectionType.OneOfOne &&
            `${price} ${currency}`}
        </Text>
      )}
    </Stack>
  );
};

export default memo(CollectionPreview);
